/*
** eAvatar - the JS representations of the c++ Avatar object
** Copyright (C) 2022-2026 Tactile Interactive, all rights reserved
*/

//import {cppObjectRegistry, prepForDirectAccessors} from '../utils/directAccessors.js';
//import eCavity from './eCavity.js';
import eObject from './eObject.js';
import qeFuncs from './qeFuncs.js';
import qeConsts from './qeConsts.js';

let traceCreation = false;
//let traceHighest = false;
let traceAttachViewBuffer = false;

// HOW TO USE     use avatars this way:
// create one with eAvatar.createAvatar()
// use attachViewBuffer() to attach each of your buffers (max 4)

// OR adapt a qAvatar (eg one on qSpace) with eAvatar.adaptAvatar()

// the integration engine should usually populate the buffers as part
// of its cycle, at the end of an integration lap.


class eAvatar extends eObject {
	// create An eAvatar complete with its inner qAvatar.  (zero buffers)
	// use this if you're doing something other than the original  space's avatars
	static createAvatar(label) {
		if (traceCreation)
			console.log(`🚦 createAvatar: lab='${label}'`);
		let qA = qeFuncs.avatar_create(label);
		return new eAvatar(qA);
	}

	// given a pointer to a qAvatar, return  an eAvatar that wraps it.
	// Including existing buffers.
	static adaptAvatar(pointer) {
		if (traceCreation)
			console.log(`🚦 adaptAvatar lab='${UTF8ToString(this._label, qeConsts.MAX_LABEL_LEN)}'`);
		let av = new eAvatar(pointer);
		av.wrapViewBuffers();
		return av;
	}

	// DO NOT USE THIS to create an eAvatar in JS!  use adaptAvatar() or
	// createAvatar() above.  To create an eAvatar, you must already have an
	// existing qAvatar and pointer to it. pointer is an integer pointing into
	// the C++ address space, to the qAvatar object. The space comes with 2
	// qAvatars already. Or, call createAvatar() above.
	constructor(pointer) {
		super();
		if (!pointer) throw Error("Need pointer to create eAvatar")
		//prepForDirectAccessors(this, pointer);
		if (!pointer || pointer < 10)
			throw "cannot construct eAvatar without qAvatar pointer: ${pointer}";
		this.setPointer(pointer);

		// for the Float32Arrays for each C++ float arrays
		this.typedArrays = new Array(4);
		this.bufferNames = new Array(4);

		this.label = UTF8ToString(this._label, qeConsts.MAX_LABEL_LEN);

		if (traceCreation)
			console.log(`🚦 cti eAvatar constructed:`, this);
	}

	// delete, except 'delete' is a reserved word.  Turn everything off. null out
	// all other JS objects and buffers it points to, so ref counting can recycle
	// it all
	liquidate() {
		throw Error("eAvatar liquidate not yet implemented")
		//this.cavity.liquidate();
		//this.space = this.cavity = this.vBuffer = null;
	}

	// set an element in the typedArray bloc
	reserveTypedArray(whichBuffer, float32) {
		if (this.typedArrays[whichBuffer])
			throw Error(`🚦 typed float32 ${whichBuffer} already reserved in ${this.label} avatar`);
		return this.typedArrays[whichBuffer] = float32;
	}

	// adds another view buffer onto the avatar.  You choose which one,
	// need not be consecutive. Try to choose a meaningful name, like the variable name in glsl.  Cannot do duplicates.  Returns the
	// typed array itself. Will ultimately hold (nCoordsPerVertex *
	// nVertices) single floats. useThisMemory is a float * pointer for
	// the array in C++ memory.  Just an integer number in JS pass it
	// if you have one, or else it'll allocate its own in C++ space.
	// allocate the index buf bloc in C++, then wrap it in a typed
	// array in JS
	attachViewBuffer(whichBuffer, useThisMemory,
				nCoordsPerVertex, nVertices, name) {
		if (traceAttachViewBuffer) dblog(`attachViewBuffer 🧶 name:${name} `
			+` whichBuffer=${whichBuffer}`)
		if (this.typedArrays[whichBuffer]) {
			console.trace(`🚦 duplicate buffer \x1b[33m name:${name} `
				+` whichBuffer:${whichBuffer} for label:${this.label}\x1b[0m`)
			throw `🚦 Second allocate of typed array buffer name:${name} `
				+` whichBuffer:${whichBuffer} for label:${this.label}`;
		}
		this.bufferNames[whichBuffer] = name;

		// buffer of floats, 4 bytes apiece.
		const nFloats = nCoordsPerVertex * nVertices;
		if (!useThisMemory)
			useThisMemory = qeFuncs.buffer_allocateBuffer(nFloats * 4);

		let vbuf = qeFuncs.avatar_attachViewBuffer(this._pointer_, whichBuffer,
				useThisMemory, nCoordsPerVertex, nVertices);
		const tArray = new Float32Array(window.Module.HEAPF32.buffer, vbuf, nFloats);
		tArray.nTuples = nVertices;  // hack so you can pass around the array and its size,
					//  and change the size from one draw to the next.  someday.

		return this.reserveTypedArray(whichBuffer, tArray);
	}

	// allocate the index buf bloc in C++, then wrap it in a typed array in JS.  Unused as of this writing.
	// attachIndexBuffer(useThisMemory, nItems) {
	//
	// 	if (this.indexBuffer)
	// 		throw `🚦 Second allocate of typed array ${this.label} buffer ${whichBuffer}`;
	//
	// 	if (!useThisMemory)
	// 		useThisMemory = qeFuncs.buffer_allocateBuffer(2 * nItems);
	//
	// 	// buffer of short ints, 16 bits apiece
	// 	const vbuf = qeFuncs.avatar_attachIndexBuffer(this._pointer_, useThisMemory, nItems);
	// 	this.indexBuffer = new Uint16Array(window.Module.HEAPU16.buffer, vbuf, nItems);
	//
	// 	return this.indexBuffer;
	// }

	// our pre-allocated qAvatar has view buffers in C++ space that we don't have
	// typed arrays for yet.  Set those up.  Called by adaptAvatar()
	wrapViewBuffers() {
		// crawl the viewBuffers array; 3 words apiece
		let nBuffers = this.nBuffers;
		const vbb = this.viewBufferBlock = new Uint32Array(
			window.Module.HEAP32.buffer, this._viewBuffers, nBuffers * 3);
			// three ints for each viewBufInfo

		for (let b = 0; b < nBuffers; b++) {
			let wordLength = vbb[b * 3 + 1] * vbb[b * 3 + 2];
			this.typedArrays[b] = new Float32Array(window.Module.HEAPF32.buffer,
				vbb[b * 3], wordLength);
		}
	}

	/* *****************************************    👽   👽    Direct Accessors */
	// see qAvatar.cpp to regenerate this. Note these are all scalars; buffers

	get _space() { return this._ints_[1]; }
	set _space(a) { this._ints_[1] = a; }
	get _cavity() { return this._ints_[2]; }
	set _cavity(a) { this._ints_[2] = a; }
	get int0() { return this._ints_[3]; }
	set int0(a) { this._ints_[3] = a; }
	get int1() { return this._ints_[4]; }
	set int1(a) { this._ints_[4] = a; }
	get double0() { return this._doubles_[3]; }
	set double0(a) { this._doubles_[3] = a; }
	get double1() { return this._doubles_[4]; }
	set double1(a) { this._doubles_[4] = a; }
	get _label() { return this._pointer_ + 40; }
	get _indexBuffer() { return this._ints_[18]; }
	set _indexBuffer(a) { this._ints_[18] = a; }
	get _nIndices() { return this._ints_[19]; }
	get _viewBuffers() { return this._pointer_ + 80; }

	/* ****************************    👽   👽    end of direct accessors */

	// this just gets the TypedArray, ready to use.  attachViewBuffer() also returns it.
	getViewBuffer(bufferIx) {
		return this.typedArrays[bufferIx];
	}

	dumpMeta(title) {
		qeFuncs.avatar_dumpMeta(this._pointer_, title);
	}

	// 0-15 for vertex bufs, any combo; 128 for index buf
	dumpEachViewBuffer(bufferMask, title) {
		console.group(title);
		qeFuncs.avatar_dumpEachViewBuffer(this._pointer_, bufferMask, title);
		console.groupEnd();
	}

	dumpComplexViewBuffer(bufIx, nPoints, title) {
		console.group(title);
		// since we already printed the title, don't do it again
		qeFuncs.avatar_dumpComplexViewBuffer(this._pointer_, bufIx, nPoints, '');
		console.groupEnd();
	}

// 	dumpIndex(title) {
// 		console.group(title);
// 		qeFuncs.avatar_dumpIndex(this._pointer_, title);
// 		console.groupEnd();
// 	}
}

export default eAvatar;

